// Senior Level Flow — pure helpers (stage machine, formatting, field mapping,
// payload shapes). Ported from the shared template.

export const norm = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// `current_status` → stage. Values are the template's examples — replace with the
// real backend enum when known. Anything unmapped falls back to field presence.
export const STATUS_STAGE = {
  new: 'intro',
  contacted: 'intro',
  'sent contact link': 'intro',
  'not interested': 'declined',
  rejected: 'closed',
  'removed from pipeline': 'closed',
  acknowledged: 'details',
  'slot assigned': 'interview',
  'interview scheduled': 'interview',
  'interview confirmed': 'interviewDone',
  'no show': 'status',
};

export function deriveStage(d) {
  if (!d) return 'intro';
  const mapped = STATUS_STAGE[norm(d.current_status)];
  if (mapped) return mapped;
  if (d.interview_datetime) return 'interview';
  // `is_active: true` means the candidate acknowledged interest but hasn't
  // finished (or may not have finished) the details step yet — send them
  // back into the form rather than the static "we have your details" panel.
  if (d.acknowledged_at) return d.is_active ? 'details' : 'status';
  return 'intro';
}

export function firstNameOf(d) {
  return String(d?.candidate_name || '').split(' ')[0] || 'there';
}

export function fmtDate(iso) {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return { when: '—', tz: '' };
  return {
    when: new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: 'numeric',
      minute: '2-digit',
    }).format(dt),
    tz: `Shown in your local time (${
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'local'
    })`,
  };
}

export function acknowledgedLabel(d) {
  if (!d?.acknowledged_at) return '';
  const dt = new Date(d.acknowledged_at);
  if (Number.isNaN(dt.getTime())) return '';
  return ` on ${new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'long',
  }).format(dt)}`;
}

// ── Formatting for the "at a glance" facts. Every value comes from a real API
// field; anything the backend leaves null is dropped, never placeheld. ──

const monthsToYears = (m) => Math.round(Number(m) / 12);

// Experience bounds are months; show years, and handle a one-sided range.
export function formatExperience(minM, maxM) {
  const hasMin = minM != null;
  const hasMax = maxM != null;
  if (!hasMin && !hasMax) return null;
  const a = hasMin ? monthsToYears(minM) : null;
  const b = hasMax ? monthsToYears(maxM) : null;
  if (hasMin && hasMax) return a === b ? `${a} years` : `${a}–${b} years`;
  if (hasMin) return `${a}+ years`;
  return `Up to ${b} years`;
}

const toLpa = (rupees) => {
  const lpa = Number(rupees) / 100000;
  return Number.isInteger(lpa) ? String(lpa) : lpa.toFixed(1);
};

// Salary bounds are rupees; show LPA, and handle a one-sided range.
export function formatPay(minS, maxS) {
  const hasMin = minS != null;
  const hasMax = maxS != null;
  if (!hasMin && !hasMax) return null;
  const a = hasMin ? toLpa(minS) : null;
  const b = hasMax ? toLpa(maxS) : null;
  if (hasMin && hasMax) return a === b ? `₹${a} LPA` : `₹${a}–${b} LPA`;
  if (hasMin) return `From ₹${a} LPA`;
  return `Up to ₹${b} LPA`;
}

// Backend import-job titles sometimes carry an internal `[IMPORTED]` marker —
// strip it so candidates never see data-pipeline plumbing.
const cleanJobTitle = (title) =>
  String(title || '').replace(/^\s*\[imported\]\s*/i, '');

// Intro data — all from real API fields. `glance` rows with no value are omitted
// (no placeholders); the full role detail is the `jd` markdown, rendered
// separately.
export function landingFields(d = {}) {
  const experience = formatExperience(
    d.min_work_experience,
    d.max_work_experience
  );
  const pay = formatPay(d.minimum_salary, d.maximum_salary);

  const glance = [
    { iconKey: 'building', label: 'Company', value: d.organization },
    { iconKey: 'pin', label: 'Location', value: d.location_name },
    { iconKey: 'briefcase', label: 'Experience', value: experience },
    { iconKey: 'wallet', label: 'Fixed pay', value: pay, mono: true },
    { iconKey: 'clock', label: 'Employment type', value: d.employment_type },
    { iconKey: 'route', label: 'Work mode', value: d.work_mode },
  ].filter((row) => row.value != null && row.value !== '');

  const subhead =
    [d.organization, d.employment_type, d.work_mode]
      .filter(Boolean)
      .join(' · ') || '';

  return {
    firstName: firstNameOf(d),
    jobTitle: cleanJobTitle(d.job_title),
    companyName: d.organization || '',
    subhead,
    glance,
    jd: d.detailed_job_description || '',
  };
}

// Single flow-scoped profile PATCH payload: one `interview_profile_update` key
// with every field flattened into one `data` object, so the whole form saves in
// a single call. The form collects CTC in lakhs; the API stores rupees.
export function buildProfilePayload(v) {
  const data = {
    notice_period_days: v.notice,
    preferred_work_locations: v.locations,
    open_to_relocation: v.relocate,
  };
  if (v.currentCtc != null)
    data.current_ctc = Math.round(v.currentCtc * 100000);
  if (v.expectedCtc != null) {
    data.expected_ctc = Math.round(v.expectedCtc * 100000);
  }
  if (v.phone) data.phone = v.phone;
  if (v.note) data.notes = v.note;
  return { key: 'interview_profile_update', data };
}
